"use client";

// Shared leaderboard table for GET /api/admin/leaderboards/{managers,salespeople}.
// Both endpoints return the same row shape, so the presentation is one component.

import Image from "next/image";
import { assetUrl, type LeaderboardRow, type PageMeta } from "../lib/api";
import { useApi } from "../lib/useApi";
import { pct } from "../lib/format";
import { Skeleton } from "./Skeleton";
import Bar from "./Bar";
import { PanelEmpty, PanelError } from "./PanelState";

function initials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

function closeRateColor(rate: number) {
  if (rate >= 40) return "var(--ok)";
  if (rate >= 25) return "var(--accent)";
  return "var(--warn)";
}

export default function LeaderboardPanel({
  title,
  subtitle,
  fetcher,
  emptyTitle,
}: {
  title: string;
  subtitle?: string;
  fetcher: (signal: AbortSignal) => Promise<{ items: LeaderboardRow[]; meta: PageMeta }>;
  emptyTitle: string;
}) {
  const { data, loading, error, reload } = useApi<{ items: LeaderboardRow[]; meta: PageMeta }>(
    fetcher,
    [title],
    "Failed to load the leaderboard.",
  );

  const rows = data?.items ?? [];

  return (
    // h-full so the panel fills its grid cell: on the Overview it sits beside
    // Recent Deals, whose row count sets the row height. Without it the shorter
    // panel floats with a ragged gap underneath.
    <div className="neu p-5 sm:p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg text-text">{title}</h2>
        <span className="text-[12px] text-text-faint">{subtitle ?? "this period"}</span>
      </div>

      {loading ? (
        // Mirrors a real row — avatar chip, name/stat line, close-rate track —
        // so the panel does not reflow when the data lands.
        <ul className="flex flex-col gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <li key={i} className="flex items-center gap-3.5">
              <Skeleton className="w-10 h-10 rounded-2xl shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <Skeleton className="h-3.5 w-28 rounded" />
                  <Skeleton className="h-3.5 w-24 rounded" />
                </div>
                {/* Real Bar at zero, same h-4 track as a loaded row so the
                    layout does not shift — it grows in when the rows land. */}
                <Bar fraction={0} loading thickness="h-4" />
                <div className="flex gap-3 mt-1.5">
                  <Skeleton className="h-3 w-12 rounded" />
                  <Skeleton className="h-3 w-14 rounded" />
                  <Skeleton className="h-3 w-10 rounded" />
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : error ? (
        <PanelError message={error} onRetry={reload} />
      ) : rows.length === 0 ? (
        <PanelEmpty title={emptyTitle} icon="users" />
      ) : (
        <ul className="flex flex-col gap-4">
          {rows.map((r) => {
            const avatar = assetUrl(r.profileImage);
            return (
              <li key={r.id} className="flex items-center gap-3.5">
                <span className="neu-sm chip grid place-items-center w-10 h-10 rounded-2xl overflow-hidden shrink-0">
                  {avatar ? (
                    <Image src={avatar} alt="" width={40} height={40} className="w-10 h-10 object-cover" />
                  ) : (
                    <span className="text-[12px] font-semibold" style={{ color: "var(--accent)" }}>
                      {initials(r.displayName) || r.rank}
                    </span>
                  )}
                </span>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-[13.5px] font-semibold text-text truncate">
                      {r.displayName}
                    </span>
                    <span className="text-[12.5px] text-text-muted tabular-nums shrink-0">
                      {pct(r.closeRate)} close · {r.totalDeals} deals
                    </span>
                  </div>
                  {/* Close-rate bar — inset track, tone by performance band */}
                  <Bar
                    fraction={r.closeRate / 100}
                    color={closeRateColor(r.closeRate)}
                    thickness="h-4"
                  />
                  <div className="flex gap-3 mt-1.5 text-[11.5px] text-text-faint tabular-nums">
                    <span>{r.activeDeals} active</span>
                    <span>{r.pendingDeals} pending</span>
                    <span style={{ color: "var(--ok)" }}>{r.soldDeals} sold</span>
                    <span style={{ color: "var(--danger)" }}>{r.lostDeals} lost</span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
